'use client';

/**
 * Calendly inline-widget embed. The Calendly script renders the iframe
 * into a div with class {@code calendly-inline-widget} + {@code data-url}.
 *
 * <p>Refuses arbitrary URLs — only ones matching the
 * {@code https://[*.]calendly.com/<path>} pattern. Closes the iframe-XSS
 * surface that "paste any URL" fields create.
 */
import { useTranslation } from 'react-i18next';
import { EmbedCard, ConfigurationError, CalendarIcon, useExternalScript } from './_shared';

interface CalendlyBlockProps {
  title?: string;
  /** Must match {@code https://[*.]calendly.com/<path>}. */
  calendlyUrl: string;
}

const CALENDLY_URL_RE = /^https:\/\/(?:[a-z0-9-]+\.)?calendly\.com\/[\w\-./?=&%#:+ ]+$/i;

export function CalendlyBlock({ calendlyUrl, title }: CalendlyBlockProps) {
  const { t } = useTranslation('site-builder-blocks');
  useExternalScript(
    CALENDLY_URL_RE.test(calendlyUrl ?? '')
      ? 'https://assets.calendly.com/assets/external/widget.js'
      : null,
  );

  if (!calendlyUrl || !CALENDLY_URL_RE.test(calendlyUrl)) {
    return <ConfigurationError message={t('embeds.calendly.invalidUrl')} />;
  }
  return (
    <EmbedCard title={title} accent={<CalendarIcon />}>
      <div
        className="calendly-inline-widget"
        data-url={calendlyUrl}
        style={{ minWidth: 320, height: 700 }}
      />
    </EmbedCard>
  );
}

export type { CalendlyBlockProps };
